'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { useNotificationsOverview } from '@/hooks/notifications/useNotificationsQuery'
import { useNotificationSoundOnArrival } from '@/hooks/notifications/useNotificationSound'
import { useBrowserNotificationDispatcher } from '@/hooks/notifications/useBrowserNotifications'
import { useNotificationsLive } from '@/hooks/notifications/useNotificationsLive'
import { BANNER_SHOW_EVENT, isBannerDismissed } from '@/lib/notifications/prefs'
import type { NotificationItem, NotificationKind } from '@/services/notifications/types'
import { NotificationModal } from './NotificationModal'
import { PermissionBanner } from './PermissionBanner'
import { MultiNotificationToast, NotificationToast } from './NotificationToast'

const SESSION_KEY = 'notifications:auto-shown'

/** Kinds que abrem o modal automático, em ordem de prioridade. Nenhum deles vira toast. */
const MODAL_KINDS: readonly NotificationKind[] = ['critical', 'marketing']

function readAutoShown(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set<string>(parsed) : new Set()
  } catch {
    return new Set()
  }
}

function persistAutoShown(set: Set<string>): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...set]))
  } catch {
    /* quota / modo privativo — no pior caso o modal reaparece na sessão */
  }
}

function browserPermissionUndecided(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) return false
  return Notification.permission === 'default'
}

/**
 * Montado **uma vez** no layout privado. Concentra os efeitos globais do
 * inbox: toast de chegada, modal de critical/Novidade, notificação nativa e o banner
 * de permissão. Nada disso pertence a uma tela — o atendente pode estar em
 * qualquer rota quando a notificação chega.
 */
export function NotificationsEffects() {
  const router = useRouter()
  const { data } = useNotificationsOverview()
  useNotificationSoundOnArrival(data?.counts.new ?? 0)
  // O inbox chega por socket, não só por poll. Aqui e não em `PrivateProviders`
  // porque este componente já é a instância única app-wide do inbox — duas
  // montagens invalidariam em dobro.
  useNotificationsLive()
  const dispatchBrowserNotif = useBrowserNotificationDispatcher()
  const items = data?.new

  // ── Banner de primeira chegada ──────────────────────────────
  const [bannerVisible, setBannerVisible] = useState(false)
  const bannerCheckedRef = useRef(false)

  useEffect(() => {
    if (bannerCheckedRef.current) return
    if (!items || items.length === 0) return
    // Só pergunta quando existe algo pra avisar — banner em tela vazia é ruído.
    bannerCheckedRef.current = true
    if (isBannerDismissed()) return
    if (!browserPermissionUndecided()) return
    setBannerVisible(true)
  }, [items])

  useEffect(() => {
    const handler = () => setBannerVisible(true)
    window.addEventListener(BANNER_SHOW_EVENT, handler)
    return () => window.removeEventListener(BANNER_SHOW_EVENT, handler)
  }, [])

  // ── Toast + notificação nativa por item novo ────────────────
  // null = ainda não inicializou (primeiro poll); Set = ids já vistos.
  const seenRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (!items) return
    const currentIds = new Set(items.map((n) => n.id))

    // Primeira leitura só semeia: senão o boot toastaria o inbox inteiro.
    if (seenRef.current === null) {
      seenRef.current = currentIds
      return
    }

    const newlyArrived: NotificationItem[] = items.filter((n) => !seenRef.current!.has(n.id))
    seenRef.current = currentIds
    if (newlyArrived.length === 0) return

    let cancelado = false

    // Assíncrono porque a notificação do SO agora passa pelo service worker e o
    // TOAST é decidido pelo resultado dela (D-2), não por `document.hidden`: quem
    // recebeu o aviso na bandeja não recebe o mesmo aviso duas vezes. Até
    // 2026-09-04 os dois saíam juntos com a aba escondida, e o toast expirava em
    // 6s sem ninguém ver.
    void (async () => {
      const semNativa: NotificationItem[] = []
      for (const n of newlyArrived) {
        const { outcome } = await dispatchBrowserNotif(n)
        // Só `sent` tira o item do lote. `skipped-tab-visible` NÃO é "saiu": é o
        // caso em que o app está na cara do usuário, e aí o toast é a superfície
        // certa — é o único motivo de a decisão vir do outcome e não da
        // visibilidade.
        if (outcome !== 'sent') semNativa.push(n)
      }
      if (cancelado) return

      // `critical` passa pelo dispatcher (as prefs POR KIND não conseguem
      // silenciá-lo — `prefs.ts:166` e `prefs.ts:204` forçam `browser: true`, e
      // `prefs.ts:240` faz o setter ser no-op; o `browserEnabled` GERAL continua
      // valendo e devolve `disabled-by-pref`) mas NUNCA entra no toast, saia a
      // nativa ou não: o canal dele é o modal bloqueante abaixo, que não expira.
      // Trocar isso por um toast de 6s rebaixaria o aviso mais grave do sistema
      // à superfície mais efêmera dele.
      // `marketing` (Novidade) idem: abre o mesmo modal automático, e um toast junto
      // seria o mesmo aviso duas vezes.
      const toastable = semNativa.filter((n) => !MODAL_KINDS.includes(n.kind))

      if (toastable.length === 1) {
        const n = toastable[0]
        toast.custom(
          (id) => (
            <NotificationToast
              toastId={id}
              notification={n}
              onClick={() => router.push(`/notifications/${n.id}`)}
            />
          ),
          { duration: 6000 },
        )
      } else if (toastable.length > 1) {
        const count = toastable.length
        toast.custom(
          (id) => (
            <MultiNotificationToast
              toastId={id}
              count={count}
              onClick={() => router.push('/notifications')}
            />
          ),
          { duration: 6000 },
        )
      }
    })()

    // O loop pode resolver depois do unmount (flush no unmount é padrão conhecido
    // deste repo). Sem a guarda, toast órfão depois de sair da rota.
    return () => {
      cancelado = true
    }
  }, [items, router, dispatchBrowserNotif])

  // ── Modal automático de critical/marketing (derivado, 1x por sessão) ──
  const [closedInSession, setClosedInSession] = useState<Set<string>>(readAutoShown)

  // Ordem de `MODAL_KINDS`, não do inbox: critical sempre antes de Novidade.
  const nextModal =
    MODAL_KINDS.map((kind) =>
      items?.find((n) => n.kind === kind && !closedInSession.has(n.id)),
    ).find((n) => n !== undefined) ?? null

  const handleModalChange = (open: boolean) => {
    if (open || !nextModal) return
    setClosedInSession((prev) => {
      const updated = new Set(prev)
      updated.add(nextModal.id)
      persistAutoShown(updated)
      return updated
    })
  }

  return (
    <>
      <NotificationModal
        notification={nextModal}
        open={nextModal !== null}
        onOpenChange={handleModalChange}
      />
      {bannerVisible && <PermissionBanner onClose={() => setBannerVisible(false)} />}
    </>
  )
}
